import {
  RefundRequest,
  ChecklistItemType,
  RefundRequestComponentType,
  RefundRequestComponentStatus,
  RefundRequestsResponse,
  RefundRequestStatusFilter,
  ActivityLog,
  RefundRequestStatusCountMap,
} from "../../common/types";
import { BudgetSummaryNumbers } from "../components/BudgetSummary";

const buildHeaders = (token: string): Headers => {
  return new Headers({
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  });
};

const buildQuery = (params: object): string => {
  let query = "";
  let paramArray: string[] = [];
  Object.keys(params).map(k => {
    const value = params[k];
    if (value) {
      if (!(k === "status" && value === "All")) {
        paramArray.push(encodeURIComponent(k) + "=" + encodeURIComponent(params[k]));
      }
    }
  });

  if (paramArray.length) {
    query = "?" + paramArray.join("&");
  }

  return query;
};

export const fetchRefundRequests = async (
  token: string,
  cursor: string,
  status: RefundRequestStatusFilter,
  backwards?: boolean
): Promise<RefundRequestsResponse> => {
  const url = `/api/refund-requests${buildQuery({ status, cursor, backwards })}`;
  const res = await fetch(url, { headers: buildHeaders(token) });
  return await res.json();
};

export const fetchRefundRequest = async (token: string, id: string): Promise<RefundRequest> => {
  const res = await fetch(`/api/refund-requests/${id}`, {
    headers: buildHeaders(token),
  });

  const resJson = await res.json();
  return resJson.body;
};

export const updateChecklistItem = async ({
  token,
  type,
  id,
  userId,
  isChecked,
  userName,
}: {
  token: string;
  type: ChecklistItemType;
  id: string;
  isChecked: boolean;
  userId: string;
  userName: string;
}): Promise<RefundRequest> => {
  const data = {
    isChecked,
    userId,
    userName,
  };

  const res = await fetch(`/api/refund-requests/${id}/checklist-items/${type}`, {
    headers: buildHeaders(token),
    method: "PUT",
    body: JSON.stringify(data),
  });

  const resJson = await res.json();

  if (!res.ok) {
    return Promise.reject(resJson.error);
  }

  return resJson.body;
};

export const updateComponent = async ({
  token,
  type,
  id,
  status,
  userId,
  userName,
}: {
  token: string;
  type: RefundRequestComponentType;
  id: string;
  status: RefundRequestComponentStatus;
  userId: string;
  userName: string;
}): Promise<RefundRequest> => {
  const data = {
    status,
    userId,
    userName,
  };

  const res = await fetch(`/api/refund-requests/${id}/components/${type}`, {
    headers: buildHeaders(token),
    method: "PUT",
    body: JSON.stringify(data),
  });

  const resJson = await res.json();

  if (!res.ok) {
    return Promise.reject(resJson.error);
  }

  return resJson.body;
};

export const deleteRefundRequest = async ({
  token,
  id,
  userId,
  userName,
}: {
  token: string;
  id: string;
  userId: string;
  userName: string;
}): Promise<RefundRequest> => {
  const data = {
    userId,
    userName,
  };
  const res = await fetch(`/api/refund-requests/${id}`, {
    headers: buildHeaders(token),
    method: "DELETE",
    body: JSON.stringify(data),
  });

  const resJson = await res.json();

  if (!res.ok) {
    return Promise.reject(resJson.error);
  }

  return resJson.body;
};

export const voidRefundRequest = async ({
  token,
  id,
  userId,
  userName,
  voidReason,
  voidNote,
}: {
  token: string;
  id: string;
  userId: string;
  userName: string;
  voidReason: string;
  voidNote: string;
}): Promise<RefundRequest> => {
  const data = {
    userId,
    userName,
    voidReason,
    voidNote,
  };
  const res = await fetch(`/api/refund-requests/${id}/void`, {
    headers: buildHeaders(token),
    method: "POST",
    body: JSON.stringify(data),
  });

  const resJson = await res.json();

  if (!res.ok) {
    return Promise.reject(resJson.error);
  }

  return resJson.body;
};

export const deleteFailedComponentsFromRefundRequest = async ({
  token,
  id,
  userId,
  userName,
}: {
  token: string;
  id: string;
  userId: string;
  userName: string;
}): Promise<RefundRequest> => {
  const data = {
    userId,
    userName,
  };
  const res = await fetch(`/api/refund-requests/${id}/delete-failed-components`, {
    headers: buildHeaders(token),
    method: "POST",
    body: JSON.stringify(data),
  });

  const resJson = await res.json();

  if (!res.ok) {
    return Promise.reject(resJson.error);
  }

  return resJson.body;
};

export const refundRefundRequest = async ({
  token,
  id,
  userId,
  userName,
}: {
  token: string;
  id: string;
  userId: string;
  userName: string;
}): Promise<RefundRequest> => {
  const data = {
    userId,
    userName,
  };
  const res = await fetch(`/api/refund-requests/${id}/refund`, {
    headers: buildHeaders(token),
    method: "POST",
    body: JSON.stringify(data),
  });

  const resJson = await res.json();

  if (!res.ok) {
    return Promise.reject(resJson.error);
  }

  return resJson.body;
};

// TODO: We should move this to another action
// Maybe actions/budget.ts?
export const fetchBudgetSummary = async ({ token }: { token: string }): Promise<BudgetSummaryNumbers> => {
  const res = await fetch(`/api/budget`, {
    headers: buildHeaders(token),
    method: "GET",
  });

  const resJson = await res.json();

  if (!res.ok) {
    return Promise.reject(resJson.error);
  }

  return resJson;
};

// TODO: We should move this to another action
// Maybe actions/budget.ts?
export const updateBudget = async ({
  token,
  budgetInCents,
}: {
  token: string;
  budgetInCents: number;
}): Promise<void> => {
  const res = await fetch(`/api/budget`, {
    headers: buildHeaders(token),
    method: "PUT",
    body: JSON.stringify({
      budget: budgetInCents,
    }),
  });

  const resJson = await res.json();

  if (!res.ok) {
    return Promise.reject(resJson.error);
  }

  return resJson;
};

export const fetchActivityLogs = async (token: string, refundRequestId: string): Promise<ActivityLog[]> => {
  const res = await fetch(`/api/refund-requests/${refundRequestId}/activity-logs`, {
    headers: buildHeaders(token),
    method: "GET",
  });

  const resJson = await res.json();

  if (!res.ok) {
    return Promise.reject(resJson.error);
  }

  return resJson.body;
};

export const fetchRefundRequestStatusCountMap = async (token: string): Promise<RefundRequestStatusCountMap> => {
  const res = await fetch(`/api/refund-requests/status-counts`, {
    headers: buildHeaders(token),
    method: "GET",
  });

  const resJson = await res.json();

  if (!res.ok) {
    return Promise.reject(resJson.error);
  }

  return resJson.body;
};

export const fetchRefundRequestsUsingSubOrderId = async (
  token: string,
  subOrderId: string
): Promise<RefundRequestsResponse> => {
  const url = `/api/refund-requests/by-sub-order-id/${subOrderId}`;
  const res = await fetch(url, { headers: buildHeaders(token) });
  return await res.json();
};
